import { useMemo } from "react";
import type { WorkspaceControlDensity } from "./workspace-control-density.js";

export interface WorkspaceControlThresholds {
  /** Width at which visible text labels become icon-only. */
  readonly labelsVisibleAt?: number;
  /** Width below which low-priority controls must live in More. */
  readonly overflowAt?: number;
  /** Width below which the adopter renders its two-row phone arrangement. */
  readonly stackedPhoneAt?: number;
  /** Descending thresholds; crossing each one increments `foldLevel`. */
  readonly foldAt?: readonly number[];
}

/**
 * Shared density and fold ladder for canvas workshop action rails. Method-owned
 * `stackedPhoneAt` values are intentionally excluded.
 */
export const CANVAS_WORKSHOP_CONTROL_THRESHOLDS = {
  labelsVisibleAt: 1180,
  overflowAt: 760,
  foldAt: [700, 560, 440],
} as const satisfies WorkspaceControlThresholds;

/** True-phone viewport boundary; work-area collision continues to use geometry. */
export const TRUE_PHONE_VIEWPORT_MAX = 450;

export interface WorkspaceControlLayoutInput {
  /** Current width of the workspace content while a rail may be moving. */
  readonly containerWidth: number;
  /** Current animated width of the rail slot. */
  readonly movingRailWidth?: number;
  /** Width the rail will occupy once its transition settles. */
  readonly railTargetWidth?: number;
  readonly thresholds?: WorkspaceControlThresholds;
}

const DEFAULT_THRESHOLDS: Required<Omit<WorkspaceControlThresholds, "foldAt">> =
  {
    labelsVisibleAt: 1280,
    overflowAt: 768,
    stackedPhoneAt: 640,
  };

/**
 * Resolve the destination layout before a right rail finishes animating.
 *
 * The content and rail widths move in opposite directions. Adding the moving
 * width reconstructs the stable workspace width; subtracting the target picks
 * the one density the controls will use for the entire transition.
 */
export function resolveWorkspaceControlLayout({
  containerWidth,
  movingRailWidth = 0,
  railTargetWidth = 0,
  thresholds,
}: WorkspaceControlLayoutInput): WorkspaceControlDensity {
  const availableWidth = Math.max(
    0,
    containerWidth + movingRailWidth - railTargetWidth,
  );
  const labelsVisibleAt =
    thresholds?.labelsVisibleAt ?? DEFAULT_THRESHOLDS.labelsVisibleAt;
  const overflowAt = thresholds?.overflowAt ?? DEFAULT_THRESHOLDS.overflowAt;
  const stackedPhoneAt =
    thresholds?.stackedPhoneAt ?? DEFAULT_THRESHOLDS.stackedPhoneAt;
  const foldAt = thresholds?.foldAt ?? [];

  return {
    labels: availableWidth >= labelsVisibleAt ? "visible" : "hidden",
    stage: availableWidth >= overflowAt ? "pill" : "overflow",
    layout: availableWidth < stackedPhoneAt ? "stacked-phone" : "single-row",
    foldLevel: foldAt.reduce(
      (level, threshold) => level + (availableWidth < threshold ? 1 : 0),
      0,
    ),
    availableWidth,
  };
}

/**
 * Horizontal room a top-row group must leave besides its own content: the two
 * 16px corner insets plus a minimum visible gap to the opposite group.
 */
export const COLLISION_EDGE_ALLOWANCE = 48;
/**
 * Extra room required before collision folding releases. Entering and exiting
 * on the same boundary would let a group that grows back on unfold re-enter
 * immediately — the ratchet is what keeps the fold from oscillating.
 */
export const COLLISION_EXIT_HYSTERESIS = 24;

export interface CollisionFoldInput {
  /** Current width of the workspace content area. */
  readonly containerWidth: number;
  /** Measured width of the opposite top-corner group (0 when absent). */
  readonly counterpartWidth: number;
  /**
   * The group's own width as last measured while NOT collision-folded — the
   * width it would want back. While folded the live width understates it.
   */
  readonly naturalWidth: number;
  /** Whether the group is currently collision-folded. */
  readonly folded: boolean;
}

/**
 * Whether a top-corner group must fold its contents into the overflow menu to
 * avoid overlapping the opposite group on the same row. Pure and hysteretic:
 * folding starts the moment both groups plus the edge allowance no longer fit,
 * and releases only once the natural width fits again with margin to spare.
 *
 * This is the shared cross-module policy recorded in ADR-0068 — the same
 * allowance and hysteresis apply wherever `WorkspaceControlGroup` is consumed.
 */
export function resolveCollisionFold({
  containerWidth,
  counterpartWidth,
  naturalWidth,
  folded,
}: CollisionFoldInput): boolean {
  if (containerWidth <= 0 || naturalWidth <= 0) return folded;
  const needed = counterpartWidth + naturalWidth + COLLISION_EDGE_ALLOWANCE;
  if (!folded) return containerWidth < needed;
  return containerWidth < needed + COLLISION_EXIT_HYSTERESIS;
}

export interface CollisionStackInput {
  /** Current width of the workspace content area. */
  readonly containerWidth: number;
  /** Measured width of the opposite top-corner group (0 when absent). */
  readonly counterpartWidth: number;
  /** The group's width while collision-folded: as narrow as it gets. */
  readonly foldedWidth: number;
  /**
   * The opposite group can still narrow itself (`data-can-shrink="true"`).
   * It gives way first, so the row drop waits. Absent, it cannot.
   */
  readonly counterpartCanShrink?: boolean;
  /** Whether the group is currently on the second row. */
  readonly stacked: boolean;
}

/**
 * Whether a collision-folded top-corner group still cannot share the row with
 * the opposite group, and must drop to a second row under it. Folding is the
 * first answer; this is the last, for when nothing is left to fold — a narrow
 * work area beside an open rail, with a top-start group that cannot shrink.
 * Same allowance and hysteresis as the fold (ADR-0068, 2026-10-09 amendment).
 *
 * A top-start group that can still give way gets to first, so the drop waits
 * for it. Leaving the second row does not: dropped, the group gives the other
 * one its whole row back and it grows again, so waiting there would loop.
 */
export function resolveCollisionStack({
  containerWidth,
  counterpartWidth,
  foldedWidth,
  counterpartCanShrink = false,
  stacked,
}: CollisionStackInput): boolean {
  if (containerWidth <= 0 || foldedWidth <= 0) return stacked;
  if (counterpartWidth <= 0) return false;
  const needed = counterpartWidth + foldedWidth + COLLISION_EDGE_ALLOWANCE;
  if (!stacked) return !counterpartCanShrink && containerWidth < needed;
  return containerWidth < needed + COLLISION_EXIT_HYSTERESIS;
}

/** Memoized React wrapper for module-owned fold ladders. */
export function useWorkspaceControlLayout(
  input: WorkspaceControlLayoutInput,
): WorkspaceControlDensity {
  const { containerWidth, movingRailWidth, railTargetWidth, thresholds } =
    input;
  return useMemo(
    () =>
      resolveWorkspaceControlLayout({
        containerWidth,
        ...(movingRailWidth != null ? { movingRailWidth } : {}),
        ...(railTargetWidth != null ? { railTargetWidth } : {}),
        ...(thresholds != null ? { thresholds } : {}),
      }),
    [containerWidth, movingRailWidth, railTargetWidth, thresholds],
  );
}
